<script setup lang="ts">
import { ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { navigateTo } from '#imports';
import { SearchInput } from '~/components/inputs/search-input';
import { AiSearchRing } from '~/components/ai-search-ring';
import SearchAiIcon from '~/assets/images/search-ai.svg?component';
import { useRecentSearches } from '~/composables/use-recent-searches';

const search = ref('');
const isExpanded = ref(false);
const containerRef = ref<HTMLElement | null>(null);

const { recentSearches, addSearch, clearSearches } = useRecentSearches();

onClickOutside(containerRef, () => {
  isExpanded.value = false;
});

const expand = async () => {
  isExpanded.value = true;
  await nextTick();
  containerRef.value?.querySelector('input')?.focus();
};

const submitSearch = (query: string) => {
  const value = query.trim();
  if (value.length === 0) return;
  addSearch(value);
  isExpanded.value = false;
  navigateTo({ path: '/search', query: { query: value } });
};
</script>

<template>
  <div ref="containerRef" class="relative">
    <AiSearchRing v-if="!isExpanded" class="shadow-md">
      <button
        type="button"
        class="bg-surface-container flex w-full items-center gap-3 rounded-full p-3 text-left"
        data-testid="ai-search-trigger"
        @click="expand"
      >
        <SearchAiIcon class="h-6 w-6 shrink-0" />
        <span class="text-on-surface-secondary text-body-md">
          {{ $t('search.placeholder') }}
        </span>
      </button>
    </AiSearchRing>

    <AiSearchRing v-else>
      <SearchInput
        v-model="search"
        ai
        placeholder="search.placeholder"
        hide-details
        class="ai-search-input"
        @keydown.enter="submitSearch(search)"
        @keydown.esc="isExpanded = false"
      />
    </AiSearchRing>

    <ClientOnly>
      <div
        v-if="isExpanded && recentSearches.length > 0"
        class="border-line bg-surface-container absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border p-2 shadow-lg"
      >
        <button
          v-for="entry of recentSearches"
          :key="entry"
          type="button"
          class="hover:bg-primary-95 text-on-surface-secondary block w-full rounded-md px-2 py-1.5 text-left text-sm"
          @click="submitSearch(entry)"
        >
          {{ entry }}
        </button>
        <button
          type="button"
          class="text-on-surface-secondary text-caption mt-1 px-2 py-1 underline"
          @click="clearSearches"
        >
          {{ $t('search.clear-recent') }}
        </button>
      </div>
    </ClientOnly>
  </div>
</template>
